import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { getStatusBadgeConfig } from "@/lib/utils/appointment-status";
import { getStatusBadgeStyle } from "@/lib/utils/clinic-appointment-status";
import { EventTooltip } from "./EventTooltip";
import type { CustomEventProps } from "./CalendarEventTypes";
import { getEventPetsLabel, getEventTypeClasses } from "./calendarEventUtils";
import { getInitials } from "@/lib/utils/string-utils";
import { renderIconBySlug } from "@/lib/event-icons";
import { AppointmentDriverBadge } from "@/components/calendar/AppointmentDriverBadge";

export function DefaultEvent({ event, statuses }: CustomEventProps) {
  const { t } = useTranslation("calendar");
  const ownerName: string | undefined = event.owner?.name;
  const statusLabel = getStatusBadgeConfig(event.status, statuses).label;
  const statusBadgeStyle = getStatusBadgeStyle(event.status, statuses);

  const getStatusBadge = (status: string) => {
    const config = getStatusBadgeConfig(status, statuses);
    return (
      <Badge
        variant="outline"
        className={cn("h-5 border-transparent px-1.5 py-0 text-[9px] font-semibold")}
        style={statusBadgeStyle}
      >
        {config.label}
      </Badge>
    );
  };

  return (
    <EventTooltip event={event} statuses={statuses}>
      <div className={cn("relative flex h-full min-h-0 flex-col gap-1 rounded-[0.625rem] p-1 pr-10 pb-6", getEventTypeClasses(event.clinic_appointment_type?.behavior ?? event.type))}>
        <div className="pointer-events-none absolute bottom-1 right-1">
          {event.vet?.full_name ? (
            <div className="pointer-events-none absolute bottom-1 left-1">
              <Avatar className="h-6 w-6">
                {event.vet.avatar_url ? (
                  <AvatarImage src={event.vet.avatar_url} alt={event.vet.full_name ?? t("professionalAlt")} />
                ) : null}
                <AvatarFallback className="text-[9px]">{getInitials(event.vet.full_name)}</AvatarFallback>
              </Avatar>
            </div>
          ) : null}
          {getStatusBadge(event.status)}
        </div>

        <div className="flex min-h-0 flex-col gap-1">
          <div className="flex items-start gap-1">
            <span className="mt-0.5 shrink-0 opacity-70">{renderIconBySlug(event.clinic_appointment_type?.icon ?? "clipboard-plus", { tone: "current", sizeClass: "h-3 w-3" })}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 flex-wrap">
                <p className="text-[11px] font-semibold leading-tight whitespace-normal break-words">{getEventPetsLabel(event)}</p>
                {event.financial_status === "pago" ? (
                  <Badge className="h-4 px-1 text-[9px] font-medium bg-emerald-700 text-white border-transparent">
                    {t("financialSettled")}
                  </Badge>
                ) : null}
                <AppointmentDriverBadge
                  isTaxiDog={event.is_taxi_dog}
                  driverType={event.taxi_dog_type}
                  iconClassName="h-2.5 w-2.5"
                />
              </div>
              {ownerName ? (
                <p className="mt-0.5 text-[10px] leading-tight opacity-80 whitespace-normal break-words">{ownerName}</p>
              ) : null}
              <p className="mt-0.5 text-[10px] leading-tight opacity-80 whitespace-normal break-words">• {statusLabel}</p>
            </div>
          </div>
        </div>
      </div>
    </EventTooltip>
  );
}
